import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API, BRAND } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Mail } from "lucide-react";

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const channel = params.get("channel") || "email";
  const [status, setStatus] = useState({ loading: !!token, ok: false, email: "" });
  const [manualEmail, setManualEmail] = useState("");
  const [manualSaving, setManualSaving] = useState(false);
  const [manualDone, setManualDone] = useState(false);

  useEffect(() => {
    if (!token) { setStatus({ loading: false, ok: false, email: "" }); return; }
    (async () => {
      try {
        const url = `${API}/unsubscribe?token=${encodeURIComponent(token)}&channel=${encodeURIComponent(channel)}`;
        const r = await fetch(url);
        const data = await r.json().catch(() => ({}));
        setStatus({ loading: false, ok: r.ok && !!data.ok, email: data.email || "" });
      } catch {
        setStatus({ loading: false, ok: false, email: "" });
      }
    })();
  }, [token, channel]);

  const manualUnsubscribe = async (e) => {
    e.preventDefault();
    if (!manualEmail.trim()) return;
    setManualSaving(true);
    try {
      const r = await fetch(`${API}/unsubscribe/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: manualEmail.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok && data.ok) { setManualDone(true); toast.success("You're unsubscribed."); }
      else toast.error(data.detail || "Could not unsubscribe");
    } catch {
      toast.error("Network error");
    } finally {
      setManualSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white relative" data-testid="unsubscribe-page">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 left-1/3 w-[500px] h-[500px] rounded-full bg-[#FF8A00]/10 blur-[160px]" />
        <div className="absolute bottom-0 -right-40 w-[420px] h-[420px] rounded-full bg-[#E52E71]/10 blur-[140px]" />
      </div>

      <div className="relative max-w-lg mx-auto pt-20 px-6 pb-24">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src={BRAND.logo} alt="" className="h-10 w-10 rounded-md" />
          <span className="font-display text-xl">Dr Thrive 365</span>
        </Link>

        {status.loading && (
          <div className="glass rounded-2xl p-8 text-white/60">Working on it…</div>
        )}

        {!status.loading && token && status.ok && (
          <div className="glass rounded-2xl p-8" data-testid="unsub-success">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center mb-5">
              <CheckCircle2 size={24}/>
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-300">You're unsubscribed</div>
            <h1 className="font-display text-3xl mt-2">We won't send you any more {channel === "sms" ? "text" : "marketing email"} updates.</h1>
            <p className="text-white/60 mt-4 leading-relaxed">
              {status.email ? `${status.email} has been removed from our marketing list. ` : ""}
              Account, billing, and booking confirmations are always sent — those aren't marketing and don't require opt-in.
            </p>
            <p className="text-white/60 mt-4 text-sm">
              Changed your mind? Log in and re-enable it under{" "}
              <Link to="/settings/notifications" className="text-[#FF8A00] hover:underline">Notifications</Link>.
            </p>
          </div>
        )}

        {!status.loading && token && !status.ok && (
          <div className="glass rounded-2xl p-8" data-testid="unsub-error">
            <div className="w-12 h-12 rounded-xl bg-white/5 text-white/50 flex items-center justify-center mb-5">
              <XCircle size={24}/>
            </div>
            <h1 className="font-display text-2xl">This link isn't valid anymore.</h1>
            <p className="text-white/60 mt-3">Not to worry — use the form below and we'll unsubscribe you by email.</p>
          </div>
        )}

        {(!token || !status.ok || manualDone) && (
          <div className="glass rounded-2xl p-8 mt-5" data-testid="unsub-manual">
            <div className="w-12 h-12 rounded-xl bg-[#FF8A00]/15 text-[#FF8A00] flex items-center justify-center mb-5">
              <Mail size={24}/>
            </div>
            <h1 className="font-display text-2xl">
              {manualDone ? "You're unsubscribed." : "Unsubscribe from Dr Thrive 365 emails."}
            </h1>
            {!manualDone && (
              <>
                <p className="text-white/60 mt-3">Enter the email you want to unsubscribe.</p>
                <form onSubmit={manualUnsubscribe} className="mt-5 flex gap-2">
                  <Input type="email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)}
                    placeholder="you@example.com" required
                    data-testid="unsub-email-input"
                    className="bg-[#0F0F0F] border-white/10"/>
                  <Button type="submit" disabled={manualSaving}
                    data-testid="unsub-email-submit"
                    className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                    {manualSaving ? "Working…" : "Unsubscribe"}
                  </Button>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
